import React from 'react';
import classNames from 'classnames';
import Portal from './../common/Portal';
import _ from 'lodash';
import TextWithEllipsis, { TextWithEllipsisFileExtension } from './TextWithEllipsis';
import Span from './elements/Span';
import { KeyCode } from '../util/Constants';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';
import { animateScrollIfNeeded, isFullscreen } from '../util/DOMUtil';
import StringUtil from '../util/StringUtil';

import './Dropdown.css';

const LEFT = 'LEFT';
const RIGHT = 'RIGHT';

type Props = {
  options: Object[] | string[],
  value: any,
  listNodeName: string,
  onChange: Function,
  onOpen: Function,
  onClose: Function,
  listOffset: number,
  anchor: string,
  hideTitleSubLabel: boolean,
  disabled: boolean,
  placeholder: string,
  clearable: boolean,
  theme: string,
  size: string,
  dismissOnScroll: any,
  ellipsis: boolean,
  constrainWidth: boolean,
  tabIndex?: number,
  name?: string,
  autocomplete?: boolean
};

type State = {
  show: boolean,
  top: number,
  bottom: ?number,
  left: number,
  right: ?number,
  width: number,
  activeIndex: number,
  autocompleteValue: string
};

class Dropdown extends React.Component<Props, State> {
  static defaultProps = {
    listNodeName: 'Dropdown_List',
    listOffset: 20,
    anchor: LEFT,
    hideTitleSubLabel: false,
    onOpen: _.noop,
    onClose: _.noop,
    placeholder: '',
    theme: 'PRIMARY',
    size: 'MEDIUM'
  };

  state = {
    show: false,
    top: 0,
    bottom: undefined,
    left: 0,
    right: undefined,
    width: 0,
    activeIndex: 0,
    autocompleteValue: ''
  };

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.autocomplete && nextProps.value && nextProps.value !== this.props.value) {
      this.setInputValue(nextProps.value);
    }
  }

  setInputValue = (value: string) => {
    const option = this.getOptions().find((o) => o.value === value);
    if (option) {
      this.setState({ autocompleteValue: option.label });
    }
  };

  componentDidMount() {
    if (this.props.value) {
      this.setInputValue(this.props.value);
      this.onChange(null, this.props.value);
    }
  }

  componentWillUnmount() {
    this.removeKeyListeners();
  }

  addKeyListeners = () => {
    KeyboardShortcuts.add(
      new KeydownConfig('DROPDOWN', this.onDropdownArrowKeyDown, [KeyCode.UP, KeyCode.DOWN], [], false)
    );
    KeyboardShortcuts.add(new KeydownConfig('DROPDOWN', this.onDropdownEnterKeyDown, [KeyCode.ENTER], [], false));
  };

  removeKeyListeners = () => {
    KeyboardShortcuts.remove('DROPDOWN');
  };

  resetValue = (event) => {
    event.stopPropagation();
    event.preventDefault();
    this.hide();
    this.props.resetValue();
  };

  getOptions = () => {
    const { options, autocomplete } = this.props;
    if (!autocomplete) {
      return options;
    }

    const { autocompleteValue } = this.state;
    return options.filter((o) => StringUtil.contains(o.label, autocompleteValue));
  };

  render() {
    const {
      options,
      value,
      listNodeName,
      hideTitleSubLabel,
      disabled,
      placeholder,
      clearable,
      theme,
      size,
      ellipsis,
      constrainWidth,
      tabIndex,
      name,
      autocomplete,
      crossImgSrc
    } = this.props;

    const { show, top, bottom, left, right, width, activeIndex, autocompleteValue } = this.state;
    const listStyle = { top, bottom, left, right };
    if (constrainWidth) {
      listStyle.width = width;
    } else {
      listStyle.minWidth = width;
    }

    const selected = _.find(options, { value });
    const label = selected ? selected.label : placeholder;
    const subLabel = selected ? selected.subLabel : '';
    const arrowImg = show ? 'images/arrow_up.svg' : 'images/arrow_down.svg';

    const rootClassName = `theme-${theme.toLowerCase()} size-${size.toLowerCase()}`;

    return (
      <div
        name="Dropdown"
        className={classNames(rootClassName, {
          'constrain-width': constrainWidth
        })}
        tabIndex={100}>
        {autocomplete ? (
          <input
            ref={(node) => (this._node = node)}
            type="text"
            value={autocompleteValue === null ? '' : autocompleteValue}
            onChange={this.onAutocompleteChange}
          />
        ) : (
          <header
            className={classNames('flex-row', 'align-center', { disabled })}
            onClick={this.show}
            ref={(node) => (this._node = node)}
            tabIndex={tabIndex}
            onKeyDown={this.onKeyDown}>
            <div
              className={classNames('title', 'flex-main', { placeholder: !selected })}
              tabIndex={this.props.tabIndexNotRequired ? null : '0'}>
              {ellipsis ? (
                <TextWithEllipsisFileExtension
                  text={label}
                  title={label}
                  style={{ margin: 'unset' }}></TextWithEllipsisFileExtension>
              ) : (
                <span className="title-label">{label}</span>
              )}
              {subLabel && !hideTitleSubLabel ? <span className="subtitle">{subLabel}</span> : null}
            </div>
            <div className="divider" />
            {clearable && selected ? (
              <img className="remove" onClick={this.resetValue} src={crossImgSrc ? crossImgSrc : 'images/remove.svg'} />
            ) : null}
            <img className="arrow" src={arrowImg} />
          </header>
        )}
        <input type="hidden" name={name} value={value ? value : ''} />
        {isFullscreen() ? (
          <div name="fullScreenDropdown">{show ? this.randerdropDownOptions() : null}</div>
        ) : (
          this.showPortal(show)
        )}
      </div>
    );
  }

  showPortal(show: boolean) {
    if (show) {
      return (
        <Portal closeOnEsc onClose={this.onClose} defaultOpen={show}>
          <div>{this.randerdropDownOptions()}</div>
        </Portal>
      );
    } else {
      return null;
    }
  }

  hide = () => {
    this.props.onClose();
    this.removeKeyListeners();
    this.setState({ show: false, top: 0, bottom: undefined, left: 0, right: undefined, width: 0 });
  };

  randerdropDownOptions() {
    const {
      options,
      value,
      listNodeName,
      hideTitleSubLabel,
      disabled,
      placeholder,
      clearable,
      theme,
      size,
      ellipsis,
      constrainWidth,
      tabIndex,
      name,
      autocomplete,
      displayable
    } = this.props;

    const { show, top, bottom, left, right, width, activeIndex, autocompleteValue } = this.state;
    const listStyle = { top, bottom, left, right };
    if (constrainWidth) {
      listStyle.width = width;
    } else {
      listStyle.minWidth = width;
    }

    if (isFullscreen()) {
      listStyle.left = 0;
    }

    const selected = _.find(options, { value });
    const label = selected ? selected.label : placeholder;
    const subLabel = selected ? selected.subLabel : '';
    const arrowImg = show ? 'images/arrow_up.svg' : 'images/arrow_down.svg';
    const rootClassName = `theme-${theme.toLowerCase()} size-${size.toLowerCase()}`;
    return (
      <div>
        {this.getOptions().length !== 0 ? (
          <div className="transparent-background" onClick={this.onBackgroundClick}>
            <ul
              className={rootClassName}
              style={listStyle}
              name="Dropdown_List"
              data-secondary-name={listNodeName}
              ref={(node) => (this._listNode = node)}>
              {this.getOptions().map((o, i) =>
                !o.disabled ? (
                  <li
                    key={o.value}
                    className={classNames('flex-row align-center spc-between', {
                      selected: o.value === value,
                      active: i === activeIndex,
                      disabled: o.disabled,
                      notdisplayable: o.displayable
                    })}
                    onClick={(e) => this.onOptionClick(e, o)}
                    tabIndex={200 + i}>
                    {o.icon ? <img src={o.icon} /> : null}
                    {o.iconCls ? <span className={o.iconCls} /> : null}
                    <div className={classNames('flex-main label-wrapper', { ellipsis })}>
                      {ellipsis ? (
                        <TextWithEllipsisFileExtension
                          text={o.label}
                          title={o.label}
                          style={{ margin: 'unset' }}></TextWithEllipsisFileExtension>
                      ) : (
                        <Span
                          className={classNames('flex-row', { label: !o.disabled, disabled: o.disabled })}
                          title={o.label}>
                          {o.label}
                        </Span>
                      )}
                    </div>
                    {o.subLabel == undefined ? null : <span className="sub-label">{o.subLabel}</span>}
                  </li>
                ) : null
              )}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  show = () => {
    const options = this.getOptions();
    const { anchor, onOpen, disabled } = this.props;
    const { show } = this.state;
    if (disabled) {
      return;
    }
    if (show) {
      return this.hide();
    }

    const rect = this._node ? this._node.getBoundingClientRect() : {};
    const top = rect.bottom > window.innerHeight - 100 ? undefined : rect.bottom + 1;
    const bottom = rect.bottom > window.innerHeight - 100 ? window.innerHeight - rect.top + 1 : undefined;
    const left = anchor === LEFT ? rect.left : undefined;
    const right = anchor === RIGHT ? window.innerWidth - rect.right : undefined;
    const width = rect.width;

    onOpen();
    this.addKeyListeners();

    let activeIndex = _.findIndex(options, { value: this.props.value });
    if (activeIndex === -1) {
      activeIndex = 0;
    }

    this.setState({ show: true, top, bottom, left, right, width, activeIndex });
  };

  onOptionClick = (e, option) => {
    e.stopPropagation();
    if (!option.disabled) {
      this.onChange(e, option.value);
    }
  };

  onChange = (e, value) => {
    if (this._node && this._node.querySelector('.title') && this._node.querySelector('.title') != null && e)
      this._node.querySelector('.title').focus();
    this.hide();
    if (value !== this.props.value) {
      this.props.onChange(value);
    }
  };

  onClose = () => {
    if (this.state.show) {
      this.hide();
    }
  };

  onBackgroundClick = () => {
    this.hide();
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === KeyCode.ENTER && this.state.show) this.hide(); 
  };

  onDropdownArrowKeyDown = (e: KeyboardEvent) => {
    const options = this.getOptions();
    const up = e.keyCode === KeyCode.UP || e.which === KeyCode.UP;
    let { activeIndex } = this.state;
    do {
      activeIndex += up ? -1 : 1;
      if (activeIndex < 0) {
        activeIndex = options.length - 1;
      } else if (activeIndex >= options.length) {
        activeIndex = 0;
      }
    } while (options[activeIndex].disabled);

    try {
      const node = this._listNode.children[activeIndex];
      animateScrollIfNeeded(this._listNode, node);
    } catch (err) {}

    this.setState({ activeIndex });
  };

  onDropdownEnterKeyDown = () => {
    const { autocomplete } = this.props;
    const { activeIndex } = this.state;
    const options = this.getOptions();
    const option = options[activeIndex];
    if (option) {
      this.onChange(null, option.value);
      if (autocomplete) {
        this.setState({ autocompleteValue: option.label });
      }
    }
  };

  updateAutoComplete = (autocompleteValue: string) => {
    const option = this.getOptions().find((o) => o.label === autocompleteValue);
    this.setState({ autocompleteValue });

    if (!this.state.show) {
      this.show();
    }
    if (option) {
      this.onChange(null, option.value);
    } else if (autocompleteValue) {
      this.props.onChange(autocompleteValue);
    } else if (this.props.value) {
      this.onChange(null, null);
    }
  };

  onAutocompleteChange = (e: Event) => {
    const autocompleteValue = e.target.value;
    this.updateAutoComplete(autocompleteValue);
  };
}

export default Dropdown;
